import * as React from "react";
import { ModelInfo, MessageFile, AttachmentRef, FulltextFileRef } from "@lupp/shared";
import VoiceRecorder from "./VoiceRecorder";
import SourcePicker from "./SourcePicker";
import { transcribeViaVoice } from "../services/voice-socket";
import { uploadFile } from "../services/upload";
import type { UploadResult } from "../services/upload";

/* ── types ───────────────────────────────────────────────── */

export type FileUploadStatus = "uploading" | "ready" | "warn-pending" | "error";

export interface AttachedFile {
  id: string;
  file: File;
  sizeLabel: string;
  status: FileUploadStatus;
  uploadResult?: UploadResult;
  includeFulltext?: boolean;
}

export interface ComposerProps {
  draft: string;
  isSending: boolean;
  isEmpty: boolean;
  model: ModelInfo;
  onDraftChange: (draft: string) => void;
  onSend: (files: MessageFile[], attachments: AttachmentRef[], fulltextFiles: FulltextFileRef[]) => void;
  onStop: () => void;
  availableSources?: AttachmentRef[];
  /** Web search is persisted per document (App-controlled). */
  webSearch: boolean;
  onToggleWebSearch: () => void;
}

interface ComposerState {
  focused: boolean;
  attachOpen: boolean;
  sourcePickerOpen: boolean;
  sourcePickerView: "knowledge" | "website";
  toolsOpen: boolean;
  recording: boolean;
  files: AttachedFile[];
  attachments: AttachmentRef[];
  spentTokens: number;
}

/* ── helpers ─────────────────────────────────────────────── */
// TODO(reactivate): vorübergehend nur die funktionierenden Einträge anbieten.
// Wieder einblenden, sobald implementiert: "Aufnahme", "Dateien anhängen",
// "Notizen anhängen", "Chats referenzieren", "Microsoft OneDrive".
const ATTACH_ITEMS = [
  { id: "upload", label: "Datei(en) hochladen", sub: false },
  { id: "webpage", label: "Webseite anhängen", sub: false },
  { id: "knowledge", label: "Wissen anhängen", sub: false },
  // { id: "record", label: "Aufnahme", sub: false },
  // { id: "files", label: "Dateien anhängen", sub: true },
  // { id: "notes", label: "Notizen anhängen", sub: true },
  // { id: "chats", label: "Chats referenzieren", sub: true },
  // { id: "onedrive", label: "Microsoft OneDrive", sub: true },
];

const DEFAULT_TOOLS = [
  { id: "search", label: "Websuche", icon: "globe", enabled: false },
  // Vorerst deaktiviert — nur Websuche aktiv:
  // { id: "image", label: "Bild", icon: "image", enabled: false },
  // { id: "code", label: "Code-Interpreter", icon: "terminal", enabled: false },
];

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* MIME → { label, iconType } */
interface MimeInfo {
  label: string;
  icon: "pdf" | "word" | "excel" | "ppt" | "image" | "text" | "csv" | "zip" | "code" | "file";
}

const MIME_MAP: Record<string, MimeInfo> = {
  "application/pdf": { label: "PDF-Dokument", icon: "pdf" },
  "application/msword": { label: "Word-Dokument", icon: "word" },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": { label: "Word-Dokument", icon: "word" },
  "application/vnd.ms-excel": { label: "Excel-Tabelle", icon: "excel" },
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": { label: "Excel-Tabelle", icon: "excel" },
  "text/csv": { label: "CSV-Datei", icon: "csv" },
  "application/vnd.ms-powerpoint": { label: "PowerPoint-Präsentation", icon: "ppt" },
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": {
    label: "PowerPoint-Präsentation",
    icon: "ppt",
  },
  "text/plain": { label: "Textdatei", icon: "text" },
  "text/markdown": { label: "Markdown-Datei", icon: "text" },
  "application/json": { label: "JSON-Datei", icon: "code" },
  "text/html": { label: "HTML-Datei", icon: "code" },
  "text/xml": { label: "XML-Datei", icon: "code" },
  "application/xml": { label: "XML-Datei", icon: "code" },
  "image/png": { label: "PNG-Bild", icon: "image" },
  "image/jpeg": { label: "JPEG-Bild", icon: "image" },
  "image/gif": { label: "GIF-Bild", icon: "image" },
  "image/svg+xml": { label: "SVG-Grafik", icon: "image" },
  "image/webp": { label: "WebP-Bild", icon: "image" },
  "application/zip": { label: "ZIP-Archiv", icon: "zip" },
  "application/x-zip-compressed": { label: "ZIP-Archiv", icon: "zip" },
};

/* fallback by extension */
const EXT_MAP: Record<string, MimeInfo> = {
  pdf: { label: "PDF-Dokument", icon: "pdf" },
  doc: { label: "Word-Dokument", icon: "word" },
  docx: { label: "Word-Dokument", icon: "word" },
  xls: { label: "Excel-Tabelle", icon: "excel" },
  xlsx: { label: "Excel-Tabelle", icon: "excel" },
  csv: { label: "CSV-Datei", icon: "csv" },
  ppt: { label: "PowerPoint-Präsentation", icon: "ppt" },
  pptx: { label: "PowerPoint-Präsentation", icon: "ppt" },
  txt: { label: "Textdatei", icon: "text" },
  md: { label: "Markdown-Datei", icon: "text" },
  json: { label: "JSON-Datei", icon: "code" },
  html: { label: "HTML-Datei", icon: "code" },
  xml: { label: "XML-Datei", icon: "code" },
  png: { label: "PNG-Bild", icon: "image" },
  jpg: { label: "JPEG-Bild", icon: "image" },
  jpeg: { label: "JPEG-Bild", icon: "image" },
  gif: { label: "GIF-Bild", icon: "image" },
  svg: { label: "SVG-Grafik", icon: "image" },
  zip: { label: "ZIP-Archiv", icon: "zip" },
};

function mimeInfo(file: File): MimeInfo {
  if (MIME_MAP[file.type]) return MIME_MAP[file.type];
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return EXT_MAP[ext] ?? { label: "Datei", icon: "file" };
}

/* ── SVG icons ───────────────────────────────────────────── */
function IconTools(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  );
}
function IconChevronRight(): JSX.Element {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}
function IconGlobe(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}
function IconImage(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  );
}
function IconTerminal(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="4 17 10 11 4 5" />
      <line x1="12" y1="19" x2="20" y2="19" />
    </svg>
  );
}
function FileTypeIcon({ type }: { type: MimeInfo["icon"] }): JSX.Element {
  const s = {
    width: 14,
    height: 14,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (type) {
    case "pdf":
      return (
        <svg {...s}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="9" y1="13" x2="15" y2="13" />
          <line x1="9" y1="17" x2="15" y2="17" />
        </svg>
      );
    case "word":
      return (
        <svg {...s}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <polyline points="9 15 12 18 15 15" />
        </svg>
      );
    case "excel":
      return (
        <svg {...s}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="8" y1="13" x2="16" y2="13" />
          <line x1="8" y1="17" x2="16" y2="17" />
          <line x1="10" y1="9" x2="14" y2="9" />
        </svg>
      );
    case "csv":
      return (
        <svg {...s}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <path d="M8 13h2m2 0h4M8 17h8" />
        </svg>
      );
    case "ppt":
      return (
        <svg {...s}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <rect x="8" y="12" width="8" height="5" rx="1" />
        </svg>
      );
    case "image":
      return (
        <svg {...s}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
      );
    case "text":
      return (
        <svg {...s}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="8" y1="13" x2="16" y2="13" />
          <line x1="8" y1="17" x2="16" y2="17" />
          <line x1="8" y1="9" x2="11" y2="9" />
        </svg>
      );
    case "code":
      return (
        <svg {...s}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <polyline points="9 15 6 18 9 21" />
          <polyline points="15 15 18 18 15 21" />
        </svg>
      );
    case "zip":
      return (
        <svg {...s}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="12" y1="12" x2="12" y2="18" />
          <line x1="9" y1="15" x2="15" y2="15" />
        </svg>
      );
    default:
      return (
        <svg {...s}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      );
  }
}

function ToolIcon({ icon }: { icon: string }): JSX.Element | null {
  if (icon === "globe") return <IconGlobe />;
  if (icon === "image") return <IconImage />;
  if (icon === "terminal") return <IconTerminal />;
  return null;
}

/* ── sub-components ──────────────────────────────────────── */

function AttachDropdown({
  onUploadClick,
  onSourcePickerOpen,
  onClose,
}: {
  onUploadClick: () => void;
  onSourcePickerOpen: (view: "knowledge" | "website") => void;
  onClose: () => void;
}): JSX.Element {
  function pick(id: string): void {
    if (id === "upload") {
      onUploadClick();
    } else if (id === "webpage") {
      onSourcePickerOpen("website");
    } else if (id === "knowledge") {
      onSourcePickerOpen("knowledge");
    }
    onClose();
  }

  return (
    <div className="c-dropdown" role="menu">
      {ATTACH_ITEMS.map((item) => (
        <button
          key={item.id}
          className="c-dropdown__item"
          type="button"
          role="menuitem"
          onClick={(): void => pick(item.id)}
        >
          <span className="c-dropdown__item-label">{item.label}</span>
          {item.sub && (
            <span className="c-dropdown__item-arrow">
              <IconChevronRight />
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

interface ToolsDropdownProps {
  tools: { id: string; label: string; icon: string; enabled: boolean }[];
  onToggle: (id: string) => void;
}

function ToolsDropdown({ tools, onToggle }: ToolsDropdownProps): JSX.Element {
  const active = tools.filter((t) => t.enabled).length;
  return (
    <div className="c-dropdown" role="menu">
      <div className="c-dropdown__header">
        <span className="c-dropdown__header-label">
          <IconTools /> Werkzeuge
        </span>
        <span className="c-dropdown__header-count">{active}</span>
        <span className="c-dropdown__item-arrow">
          <IconChevronRight />
        </span>
      </div>
      {tools.map((tool) => (
        <button
          key={tool.id}
          className="c-dropdown__item c-dropdown__item--tool"
          type="button"
          role="menuitem"
          onClick={(): void => onToggle(tool.id)}
        >
          <span className="c-dropdown__tool-icon">
            <ToolIcon icon={tool.icon} />
          </span>
          <span className="c-dropdown__item-label">{tool.label}</span>
          <span className={`c-toggle${tool.enabled ? " c-toggle--on" : ""}`} aria-hidden="true">
            <span className="c-toggle__knob" />
          </span>
        </button>
      ))}
    </div>
  );
}

interface FileChipProps {
  af: AttachedFile;
  onRemove: (id: string) => void;
  onConfirmFulltext: (id: string, include: boolean) => void;
}

function FileChip({ af, onRemove, onConfirmFulltext }: FileChipProps): JSX.Element {
  const info = mimeInfo(af.file);
  const short = af.file.name.length > 14 ? af.file.name.slice(0, 12) + "…" : af.file.name;

  let statusIndicator: JSX.Element | null = null;
  if (af.status === "uploading") {
    statusIndicator = <span className="file-chip__status file-chip__status--uploading" title="Hochladen…">⋯</span>;
  } else if (af.status === "error") {
    statusIndicator = <span className="file-chip__status file-chip__status--error" title="Fehler beim Hochladen">!</span>;
  } else if (af.status === "warn-pending") {
    statusIndicator = (
      <span className="file-chip__status file-chip__status--warn" title="Dokument groß — Volltext optional">
        <button type="button" className="file-chip__fulltext-yes" onClick={(): void => onConfirmFulltext(af.id, true)} title="Volltext einschließen">V+</button>
        <button type="button" className="file-chip__fulltext-no" onClick={(): void => onConfirmFulltext(af.id, false)} title="Nur Indizierung">V-</button>
      </span>
    );
  } else if (af.status === "ready" && af.uploadResult?.policy === "none") {
    statusIndicator = <span className="file-chip__status file-chip__status--none" title="Budget überschritten — nur Indizierung">i</span>;
  }

  return (
    <div className={`file-chip file-chip--${af.status}`} title={af.file.name}>
      <span className="file-chip__icon" title={info.label}>
        <FileTypeIcon type={info.icon} />
      </span>
      <span className="file-chip__name">{short}</span>
      <span className="file-chip__size">{af.sizeLabel}</span>
      {statusIndicator}
      <button
        className="file-chip__remove"
        type="button"
        aria-label={`${af.file.name} entfernen`}
        onClick={(): void => onRemove(af.id)}
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

/** Glühbirne ("Idee") — Icon für angehängte Wissensquellen (Knowledge Bases). */
function LightbulbIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5.76.76 1.23 1.52 1.41 2.5" />
    </svg>
  );
}

/** Globus — Icon für angehängte Webseiten/Links. */
function LinkIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

/* ── Composer ────────────────────────────────────────────── */
export default class Composer extends React.Component<ComposerProps, ComposerState> {
  private textareaRef = React.createRef<HTMLTextAreaElement>();
  private fileInputRef = React.createRef<HTMLInputElement>();
  private attachRef = React.createRef<HTMLDivElement>();
  private toolsRef = React.createRef<HTMLDivElement>();

  constructor(props) {
    super(props);
    this.state = {
      focused: false,
      attachOpen: false,
      sourcePickerOpen: false,
      sourcePickerView: "knowledge",
      toolsOpen: false,
      recording: false,
      files: [],
      attachments: [],
      spentTokens: 0,
    };
  }

  componentDidMount(): void {
    document.addEventListener("mousedown", this.handleOutside);
  }
  componentWillUnmount(): void {
    document.removeEventListener("mousedown", this.handleOutside);
  }

  componentDidUpdate(prev: ComposerProps): void {
    if (prev.draft !== this.props.draft) {
      requestAnimationFrame(() => {
        const el = this.textareaRef.current;
        if (el) {
          el.style.height = "auto";
          el.style.height = `${Math.min(el.scrollHeight, Math.floor(window.innerHeight / 3))}px`;
        }
      });
    }
  }

  handleOutside = (e: MouseEvent): void => {
    const t = e.target as Node;
    if (this.attachRef.current && !this.attachRef.current.contains(t)) {
      this.setState({ attachOpen: false });
    }
    if (this.toolsRef.current && !this.toolsRef.current.contains(t)) {
      this.setState({ toolsOpen: false });
    }
  };

  doSend = (): void => {
    const { files, attachments } = this.state;

    // Uploaded files are surfaced ONLY as attachments (type "file") — like every other
    // source. We carry the formatted size so the attachment chip can show it.
    const uploadedAttachments: AttachmentRef[] = files
      .filter((af) => af.status === "ready" || af.status === "warn-pending")
      .filter((af) => af.uploadResult)
      .map((af) => ({ type: "file" as const, id: af.uploadResult!.fileId, name: af.file.name, size: af.sizeLabel }));

    // Collect fulltext files for context injection
    const fulltextFiles: FulltextFileRef[] = files
      .filter((af) => af.uploadResult?.text)
      .filter((af) => {
        if (af.uploadResult!.policy === "auto") return true;
        if (af.uploadResult!.policy === "warn") return af.includeFulltext === true;
        return false;
      })
      .map((af) => ({
        id: af.uploadResult!.fileId,
        name: af.file.name,
        text: af.uploadResult!.text!,
      }));

    this.props.onSend([], [...attachments, ...uploadedAttachments], fulltextFiles);
    this.setState({ files: [], attachments: [], spentTokens: 0 });
  };

  addAttachment = (ref: AttachmentRef): void => {
    this.setState((s) => ({
      attachments: [...s.attachments, ref],
      sourcePickerOpen: false,
    }));
  };

  removeAttachment = (index: number): void => {
    this.setState((s) => ({
      attachments: s.attachments.filter((_, i) => i !== index),
    }));
  };

  handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.doSend();
    }
  };

  handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const el = e.currentTarget;
    el.style.height = "auto";
    const maxH = Math.floor(window.innerHeight / 3);
    el.style.height = `${Math.min(el.scrollHeight, maxH)}px`;
    this.props.onDraftChange(el.value);
  };

  openFilePicker = (): void => {
    this.fileInputRef.current?.click();
  };

  handleFileInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files && e.target.files.length > 0) {
      const picked = Array.from(e.target.files);
      const newChips: AttachedFile[] = picked.map((f) => ({
        id: `${f.name}-${Date.now()}-${Math.random()}`,
        file: f,
        sizeLabel: fmtSize(f.size),
        status: "uploading" as const,
      }));
      this.setState(
        (s) => ({ files: [...s.files, ...newChips] }),
        () => {
          for (const chip of newChips) {
            this.uploadChip(chip.id);
          }
        },
      );
    }
    e.target.value = "";
  };

  uploadChip = (chipId: string): void => {
    const chip = this.state.files.find((f) => f.id === chipId);
    if (!chip) return;

    uploadFile(chip.file, this.state.spentTokens)
      .then((result) => {
        this.setState((s) => {
          const newStatus: FileUploadStatus = result.policy === "warn" ? "warn-pending" : "ready";
          const newSpent = result.policy === "auto" ? s.spentTokens + result.tokenEstimate : s.spentTokens;
          return {
            spentTokens: newSpent,
            files: s.files.map((f) =>
              f.id === chipId ? { ...f, status: newStatus, uploadResult: result } : f,
            ),
          };
        });
      })
      .catch(() => {
        this.setState((s) => ({
          files: s.files.map((f) => (f.id === chipId ? { ...f, status: "error" as const } : f)),
        }));
      });
  };

  removeFile = (id: string): void => {
    this.setState((s) => ({ files: s.files.filter((f) => f.id !== id) }));
  };

  confirmFulltext = (id: string, include: boolean): void => {
    this.setState((s) => ({
      files: s.files.map((f) =>
        f.id === id
          ? { ...f, status: "ready" as const, includeFulltext: include }
          : f,
      ),
    }));
  };

  toggleTool = (id: string): void => {
    if (id === "search") this.props.onToggleWebSearch();
  };

  render(): JSX.Element {
    const { draft, isSending, isEmpty, model, webSearch } = this.props;
    const { focused, attachOpen, sourcePickerOpen, toolsOpen, recording, files, attachments } = this.state;
    const canSend = draft.trim().length > 0 && !isSending;
    const active = draft.length > 0 || focused || files.length > 0 || attachments.length > 0;
    // Only web search remains; its enabled state is App-controlled (persisted per document).
    const tools = DEFAULT_TOOLS.map((t) => (t.id === "search" ? { ...t, enabled: webSearch } : t));
    const activeToolCount = tools.filter((t) => t.enabled).length;

    /* ── Voice recording mode ── */
    if (recording) {
      return (
        <div className="composer">
          <VoiceRecorder
            onCancel={(): void => this.setState({ recording: false })}
            onConfirm={async (blob, ext): Promise<void> => {
              this.setState({ recording: false });
              try {
                const text = await transcribeViaVoice(blob, ext);
                if (text) {
                  const current = this.props.draft;
                  const separator = current.trim().length > 0 ? " " : "";
                  this.props.onDraftChange(current + separator + text);
                }
              } catch (err) {
                console.error("Transcription error:", err);
              }
            }}
          />
        </div>
      );
    }

    return (
      <div className={`composer${isEmpty ? " composer--hero" : ""}`}>
        {isEmpty && (
          <div className="composer__hero">
            <div className="composer__model-icon" aria-hidden="true">
              {model.iconUrl ? (
                <img src={model.iconUrl} alt="" width="42" height="42" style={{ objectFit: "contain" }} />
              ) : (
                <svg width="42" height="42" viewBox="0 0 42 42" fill="none">
                  <rect width="42" height="42" rx="11" fill="#2563eb" />
                  <path
                    d="M12 21l6 6 12-12"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            <h2 className="composer__model-name">{model.name}</h2>
            <p className="composer__model-desc">{model.description}</p>
          </div>
        )}

        {/* hidden file input — lives outside the dropdown so it survives close */}
        <input
          ref={this.fileInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={this.handleFileInput}
        />

        <div className={`composer__box${active ? " composer__box--active" : ""}`}>
          {/* file + source chips — flow together in one wrapping row */}
          {(files.length > 0 || attachments.length > 0) && (
            <div className="composer__files">
              {files.map((af) => (
                <FileChip key={af.id} af={af} onRemove={this.removeFile} onConfirmFulltext={this.confirmFulltext} />
              ))}
              {attachments.map((att, i) => {
                const label = att.type === "text" ? att.url : att.name ?? att.id;
                const key = att.type === "text" ? att.url : att.id;
                const short = label.length > 14 ? label.slice(0, 12) + "…" : label;
                const isWebsite = att.type === "text";
                return (
                  <div key={`att-${key}-${i}`} className="file-chip file-chip--ready" title={label}>
                    <span className="file-chip__icon" title={isWebsite ? "Webseite" : "Wissensquelle"}>
                      {isWebsite ? <LinkIcon /> : <LightbulbIcon />}
                    </span>
                    <span className="file-chip__name">{short}</span>
                    <button
                      className="file-chip__remove"
                      type="button"
                      aria-label={`${label} entfernen`}
                      onClick={(): void => this.removeAttachment(i)}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* source picker overlay */}
          {sourcePickerOpen && (
            <div className="composer__source-picker-overlay">
              <SourcePicker
                initialView={this.state.sourcePickerView}
                onAdd={this.addAttachment}
                onClose={(): void => this.setState({ sourcePickerOpen: false })}
              />
            </div>
          )}

          <textarea
            ref={this.textareaRef}
            className="composer__textarea"
            value={draft}
            rows={1}
            placeholder="Nachricht eingeben …"
            onChange={this.handleChange}
            onKeyDown={this.handleKeyDown}
            onFocus={(): void => this.setState({ focused: true })}
            onBlur={(): void => this.setState({ focused: false })}
            aria-label="Nachricht eingeben"
          />

          <div className="composer__toolbar">
            {/* left: attach + tools */}
            <div className="composer__toolbar-left">
              {/* attach */}
              <div className="c-popover" ref={this.attachRef}>
                <button
                  className="icon-btn"
                  type="button"
                  aria-label="Anhang hinzufügen"
                  aria-expanded={attachOpen}
                  onClick={(): void => this.setState((s) => ({ attachOpen: !s.attachOpen, toolsOpen: false }))}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </button>
                {attachOpen && (
                  <AttachDropdown
                    onUploadClick={this.openFilePicker}
                    onSourcePickerOpen={(view): void => this.setState({ sourcePickerOpen: true, sourcePickerView: view })}
                    onClose={(): void => this.setState({ attachOpen: false })}
                  />
                )}
              </div>

              {/* separator */}
              <span className="composer__sep" aria-hidden="true" />

              {/* tools */}
              <div className="c-popover" ref={this.toolsRef}>
                <button
                  className="icon-btn"
                  type="button"
                  aria-label="Werkzeuge"
                  aria-expanded={toolsOpen}
                  onClick={(): void => this.setState((s) => ({ toolsOpen: !s.toolsOpen, attachOpen: false }))}
                >
                  <IconTools />
                  <span className="composer__tools-count">{activeToolCount}</span>
                </button>
                {toolsOpen && <ToolsDropdown tools={tools} onToggle={this.toggleTool} />}
              </div>

              {/* active web-search chip — click disables it; hover reveals the ✕ */}
              {webSearch && (
                <button
                  type="button"
                  className="web-search-chip group"
                  aria-label="Websuche deaktivieren"
                  title="Websuche aktiv — klicken zum Deaktivieren"
                  onClick={this.props.onToggleWebSearch}
                >
                  <svg
                    className="web-search-chip__globe"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    width="16"
                    height="16"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"
                    />
                  </svg>
                  <span className="web-search-chip__x" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                    </svg>
                  </span>
                </button>
              )}
            </div>

            {/* right: mic + send/stop */}
            <div className="composer__toolbar-right">
              {/* mic always visible — recording works even while AI is responding */}
              <button
                className="composer__mic"
                type="button"
                aria-label="Spracheingabe"
                title="Spracheingabe"
                onClick={(): void => this.setState({ recording: true, attachOpen: false, toolsOpen: false })}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 1a4 4 0 0 1 4 4v7a4 4 0 0 1-8 0V5a4 4 0 0 1 4-4z" />
                  <path
                    d="M19 10v2a7 7 0 0 1-14 0v-2"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <line x1="12" y1="19" x2="12" y2="22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
              {isSending ? (
                <button
                  className="composer__stop"
                  type="button"
                  onClick={this.props.onStop}
                  aria-label="Anfrage abbrechen"
                  title="Abbrechen"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <rect x="4" y="4" width="16" height="16" rx="2" />
                  </svg>
                </button>
              ) : (
                <button
                  className="composer__send"
                  type="button"
                  disabled={!canSend}
                  onClick={this.doSend}
                  aria-label="Senden"
                  title="Senden (Enter)"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
